/**
 * El escritorio del ERP.
 *
 * Barra lateral por dominio, migas siempre presentes y la empresa visible
 * en la cabecera sin excepción: ERP-UX.md §1 lo pide con una razón
 * concreta —«trabajar en la empresa equivocada es un incidente»— y en un
 * sistema que emite actas y facturas ese incidente sale caro.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { hasPermission, useSession } from '../session/session-context.js';
import { describeScope, useWorkspace } from '../session/workspace.js';
import { activeGroupId, breadcrumbsFor, visibleNavigation } from './navigation.js';
import { CompanySwitcher } from './company-switcher.js';
import { CommandPalette } from './command-palette.js';
import { NavIcon } from './nav-icons.js';

/** Preferencia de interfaz, no dato de sesión: en `localStorage` está bien. */
const SIDEBAR_KEY = 'fibwifire.sidebar.collapsed';
/**
 * Qué sección del menú queda desplegada. Misma razón.
 *
 * Es una sola, no un conjunto: abrir una cierra la anterior. Con trece
 * secciones, permitir varias abiertas devuelve el menú al tamaño que el
 * plegado venía a arreglar.
 */
const NAV_GROUP_KEY = 'fibwifire.sidebar.group';

export function AppShell(): React.ReactNode {
  const { user, api, logout } = useSession();
  const workspace = useWorkspace(api);
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === '1');
  const [openGroup, setOpenGroup] = useState<string | null>(() =>
    localStorage.getItem(NAV_GROUP_KEY),
  );

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    if (openGroup === null) localStorage.removeItem(NAV_GROUP_KEY);
    else localStorage.setItem(NAV_GROUP_KEY, openGroup);
  }, [openGroup]);

  const can = useCallback(
    (permission: string) => hasPermission(user, permission),
    [user],
  );

  const groups = visibleNavigation(can);
  const crumbs = breadcrumbsFor(location.pathname);
  const activeGroup = activeGroupId(location.pathname);

  // Al navegar, se despliega la sección de destino y se cierra la anterior.
  // No pisa una elección manual: sólo se dispara cuando cambia la sección
  // activa, así que abrir «Almacén» mientras se trabaja en «Clientes» aguanta.
  useEffect(() => {
    if (activeGroup !== null) setOpenGroup(activeGroup);
  }, [activeGroup]);

  const toggleGroup = useCallback((id: string) => {
    setOpenGroup((current) => (current === id ? null : id));
  }, []);

  return (
    <div className={`shell${collapsed ? ' shell--collapsed' : ''}`}>
      <header className="topbar">
        <button
          className="topbar__toggle"
          type="button"
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Desplegar el menú' : 'Plegar el menú'}
          onClick={() => setCollapsed((c) => !c)}
        >
          ☰
        </button>

        <Link className="topbar__brand" to="/" aria-label="fibwiFIRE — ir al panel">
          {/* El SVG va como `img` y no incrustado: trae sus propias clases
              `.cls-1`… y, dentro del documento, chocarían con las de la
              página. Además así lo cachea el navegador aparte del bundle. */}
          <img className="topbar__logo" src="/logo.svg" alt="fibwiFIRE" />
        </Link>

        {workspace && (
          <>
            <CompanySwitcher workspace={workspace} onSwitch={() => void logout()} />
            <span className="topbar__scope" title="Ámbito de delegación de esta sesión">
              {describeScope(workspace)}
            </span>
          </>
        )}

        <div className="topbar__spacer" />

        {/* Pista visible del atajo: un acelerador que nadie descubre no
            acelera nada. */}
        <span className="topbar__kbd" aria-hidden="true">⌘K</span>

        {workspace && (
          // El propio nombre es el acceso a la cuenta. Es donde la gente lo
          // busca, y evita meter «Mi cuenta» en el menú lateral, que exige
          // declarar un permiso: cambiar la propia contraseña no debe
          // depender de tener ninguno.
          <Link className="topbar__user" to="/mi-cuenta" title={workspace.user.email}>
            {workspace.user.fullName}
          </Link>
        )}
        <button className="button button--quiet" type="button" onClick={() => void logout()}>
          Salir
        </button>
      </header>

      <nav className="sidebar" aria-label="Secciones">
        {groups.map((group) => {
          // Un grupo de una sola opción se pinta como enlace directo. Plegarlo
          // añadiría un clic para descubrir lo que ya cabía en la misma línea,
          // y son seis de los trece: es la mitad de la longitud del menú.
          if (group.items.length === 1) {
            const item = group.items[0]!;
            return (
              <div className="sidebar__group" key={group.id}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `sidebar__item sidebar__item--solo${isActive ? ' sidebar__item--active' : ''}`
                  }
                >
                  <NavIcon groupId={group.id} />
                  {item.label}
                </NavLink>
              </div>
            );
          }

          const open = openGroup === group.id;

          return (
            <div className="sidebar__group" key={group.id}>
              <button
                type="button"
                className="sidebar__label sidebar__label--toggle"
                aria-expanded={open}
                aria-controls={`nav-${group.id}`}
                onClick={() => toggleGroup(group.id)}
              >
                <span className="sidebar__chevron" aria-hidden="true">
                  {open ? '▾' : '▸'}
                </span>
                <NavIcon groupId={group.id} />
                {group.label}
              </button>

              {open && (
                <div className="sidebar__items" id={`nav-${group.id}`}>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/'}
                      className={({ isActive }) =>
                        `sidebar__item${isActive ? ' sidebar__item--active' : ''}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <CommandPalette />

      <div className="content">
        <nav className="crumbs" aria-label="Ruta de navegación">
          {crumbs.map((crumb, index) => (
            <span className="crumbs__step" key={`${crumb.label}-${index}`}>
              {index > 0 && <span className="crumbs__sep" aria-hidden="true">›</span>}
              {crumb.path ? (
                <Link to={crumb.path}>{crumb.label}</Link>
              ) : (
                <span aria-current={index === crumbs.length - 1 ? 'page' : undefined}>
                  {crumb.label}
                </span>
              )}
            </span>
          ))}
        </nav>

        <main className="content__body">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
